import React, { useEffect, useState, useRef, createElement } from 'react';
import { UploadCloud } from 'lucide-react';
import { EffectParams, effects } from '../lib/effects';
interface RenderAreaProps {
  svgUrl: string | null;
  activeEffectId: string;
  effectParams: EffectParams;
  onUpload: (file: File) => void;
  onProcessedCanvasReady: (canvas: HTMLCanvasElement) => void;
}
export function RenderArea({
  svgUrl,
  activeEffectId,
  effectParams,
  onUpload,
  onProcessedCanvasReady
}: RenderAreaProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const sourceCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Flips true once the SVG has been rasterized into the offscreen source
  // canvas. The animation loop keys off this so it starts AFTER the async
  // image load completes, instead of missing it on the initial render.
  const [sourceReady, setSourceReady] = useState(false);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'image/svg+xml') {
        onUpload(file);
      } else {
        alert('Please upload an SVG file.');
      }
    }
  };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  };
  const paramsRef = useRef(effectParams);
  const effectIdRef = useRef(activeEffectId);
  useEffect(() => {
    paramsRef.current = effectParams;
    effectIdRef.current = activeEffectId;
    // Always render an immediate frame when the selected effect or its params
    // change. This guarantees an effect switch is visible instantly, whether
    // or not the continuous animation loop happens to be running.
    if (sourceCanvasRef.current) {
      applyEffects(0, activeEffectId, effectParams);
    }
  }, [effectParams, activeEffectId, sourceReady]);
  useEffect(() => {
    let animationFrameId: number;
    let startTime = performance.now();
    let lastDrawTime = 0;
    let accumulatedTime = 0;
    let lastFrameTime = performance.now();
    const renderLoop = (time: number) => {
      const deltaTime = time - lastFrameTime;
      lastFrameTime = time;
      if (paramsRef.current.playing) {
        accumulatedTime += deltaTime;
      }
      if (time - lastDrawTime > 33) {
        // Cap at ~30fps
        applyEffects(accumulatedTime, effectIdRef.current, paramsRef.current);
        lastDrawTime = time;
      }
      if (paramsRef.current.playing) {
        animationFrameId = requestAnimationFrame(renderLoop);
      }
    };
    if (effectParams.playing && sourceReady && sourceCanvasRef.current) {
      lastFrameTime = performance.now();
      animationFrameId = requestAnimationFrame(renderLoop);
    }
    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [effectParams.playing, svgUrl, sourceReady]);
  useEffect(() => {
    if (!svgUrl) return;
    // New source incoming — mark it not-ready until the raster is prepared.
    setSourceReady(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const maxSide = 800;
      let contentW = img.width;
      let contentH = img.height;
      if (contentW > maxSide || contentH > maxSide) {
        if (contentW > contentH) {
          contentH = Math.round(contentH * maxSide / contentW);
          contentW = maxSide;
        } else {
          contentW = Math.round(contentW * maxSide / contentH);
          contentH = maxSide;
        }
      }
      // Pad the raster with transparent breathing room so effects that
      // displace pixels outward (chromatic shift, molten warp, glow, metal
      // sheen) have somewhere to render instead of clipping at the edge.
      const pad = Math.round(Math.max(contentW, contentH) * 0.22);
      const width = contentW + pad * 2;
      const height = contentH + pad * 2;
      const offscreen = document.createElement('canvas');
      offscreen.width = width;
      offscreen.height = height;
      const ctx = offscreen.getContext('2d', {
        willReadFrequently: true
      });
      if (!ctx) return;
      // Draw the logo centered within the padded canvas.
      ctx.drawImage(img, pad, pad, contentW, contentH);
      sourceCanvasRef.current = offscreen;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = width;
        canvas.height = height;
      }
      applyEffects(0, effectIdRef.current, paramsRef.current);
      // Source raster is ready: this both renders the first frame above and
      // triggers the animation-loop effect (which depends on sourceReady) so
      // playback actually starts on initial load, not just after a re-upload.
      setSourceReady(true);
    };
    img.src = svgUrl;
  }, [svgUrl]);
  const applyEffects = (timeMs: number, currentEffectId: string, currentParams: EffectParams) => {
    if (!sourceCanvasRef.current || !canvasRef.current) return;
    try {
      const source = sourceCanvasRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', {
        willReadFrequently: true
      });
      if (!ctx) return;
      const w = canvas.width;
      const h = canvas.height;
      // Clear and draw background
      ctx.clearRect(0, 0, w, h);
      if (currentParams.bgColor !== 'transparent') {
        ctx.fillStyle = currentParams.bgColor;
        ctx.fillRect(0, 0, w, h);
      }
      const activeEffect = effects.find(e => e.id === currentEffectId) || effects[0];
      activeEffect.render(ctx, source, w, h, timeMs, currentParams);
      onProcessedCanvasReady(canvas);
    } catch (e) {
      console.error('Processing error', e);
    }
  };
  if (!svgUrl) {
    return <div className={`absolute inset-0 flex items-center justify-center p-8 transition-colors ${isDragging ? 'bg-indigo-50' : 'bg-slate-50'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-61">
        <label className={`flex flex-col items-center justify-center w-full max-w-lg h-64 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${isDragging ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-300 hover:bg-slate-100'}`} data-id="element-62">
          <div className="flex flex-col items-center justify-center pt-5 pb-6" data-id="element-63">
            <UploadCloud className={`w-10 h-10 mb-3 ${isDragging ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-64" />
            <p className="mb-2 text-sm text-slate-600 font-medium" data-id="element-65">
              <span className="font-semibold text-indigo-600" data-id="element-66">
                Click to upload
              </span>{' '}
              or drag and drop
            </p>
            <p className="text-xs text-slate-500" data-id="element-67">SVG files only</p>
          </div>
          <input type="file" className="hidden" accept=".svg" onChange={handleFileInput} data-id="element-68" />
        </label>
      </div>;
  }
  return <div className="absolute inset-0 overflow-hidden bg-slate-100 flex items-center justify-center p-8" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-69">
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none" style={{
      backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
      backgroundSize: '20px 20px',
      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
    }} data-id="element-70" />

      <button type="button" onClick={() => inputRef.current?.click()} className="absolute bottom-4 left-4 z-20 flex items-center gap-1.5 rounded-2xl bg-white/80 backdrop-blur-xl px-3.5 py-2 text-xs font-medium text-slate-600 shadow-lg shadow-slate-900/5 ring-1 ring-slate-900/5 hover:text-indigo-600 transition-colors" data-id="element-71">
        <UploadCloud className="w-3.5 h-3.5" data-id="element-72" />
        Replace SVG
      </button>
      <input ref={inputRef} type="file" className="hidden" accept=".svg" onChange={handleFileInput} data-id="element-73" />

      {isDragging && <div className="absolute inset-3 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-indigo-500 bg-indigo-50/80 backdrop-blur-sm pointer-events-none" data-id="element-74">
          <div className="flex flex-col items-center text-indigo-600" data-id="element-75">
            <UploadCloud className="w-8 h-8 mb-2" data-id="element-76" />
            <p className="text-sm font-semibold" data-id="element-77">Drop to replace SVG</p>
          </div>
        </div>}

      <div className="relative z-10 max-w-full max-h-full flex items-center justify-center" data-id="element-78">
        <canvas ref={canvasRef} className="max-w-full max-h-full object-contain" data-id="element-79" />
        {isProcessing && <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center" data-id="element-80">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-81" />
          </div>}
      </div>
    </div>;
}